import { Injectable } from '@angular/core';
import {
	PluginType,
	PluginFAQGameData,
	FAQItem,
} from '@meta-press/shared-plugins';
import {
	GenerationRequest,
	GenerationResult,
	GenerationPrompts,
	ValidationResult,
} from './base/game-generation.interface';
import { BaseGenerationService } from './base/base-generation.service';
import { SettingsService } from '../settings-service/settings.service';
import { v4 as uuidv4 } from 'uuid';

/**
 * FAQ specific generation service
 */
@Injectable({
	providedIn: 'root',
})
export class FAQGenerationService extends BaseGenerationService<PluginFAQGameData> {
	readonly pluginType = PluginType.FAQ;

	/**
	 * Validate the generation request
	 */
	validateRequest(request: GenerationRequest): ValidationResult {
		if (!request.user) {
			return { valid: false, error: 'User information is required' };
		}

		// Check if we have either source material or a topic
		const hasSourceMaterial =
			request.sourceMaterial &&
			request.sourceMaterial.selectedArticles &&
			request.sourceMaterial.selectedArticles.length > 0;

		const hasTopic =
			request.parameters?.questionTopic &&
			request.parameters.questionTopic.trim().length > 0;

		if (!hasSourceMaterial && !hasTopic) {
			return {
				valid: false,
				error: 'Either source material or a topic is required for FAQ generation',
			};
		}

		return { valid: true };
	}

	/**
	 * Create generation prompts for FAQ
	 */
	createPrompts(
		request: GenerationRequest,
		settingsService: SettingsService,
	): GenerationPrompts {
		const targetLanguagePrompt = settingsService.GetTargetLanguagePrompt();
		const numberOfQuestions = request.parameters?.numberOfQuestions || 5;
		const topic = request.parameters?.questionTopic || '';

		const systemPrompt = `Olet FAQ-sisällön asiantuntija. Tehtäväsi on luoda usein kysyttyjä kysymyksiä ja vastauksia annetusta materiaalista.

Luo selkeitä, hyödyllisiä ja informatiivisia FAQ-kysymyksiä ja -vastauksia. Jokainen FAQ-kohta sisältää:
1. Luonnollisen kysymyksen
2. Lyhyen ytimekkään vastauksen (1-2 lausetta)
3. Laajemman yksityiskohtaisen vastauksen (5-10 lausetta)

${targetLanguagePrompt}

Vastaa AINA JSON-muodossa:
{
  "faqItems": [
    {
      "id": "unique-id",
      "question": "Kysymys tässä?",
      "shortAnswer": "Lyhyt vastaus tässä.",
      "expandedAnswer": "Pidempi ja yksityiskohtaisempi vastaus tässä."
    }
  ],
  "title": "Usein kysyttyä"
}`;

		const includeImages = request.sourceMaterial?.imagesAsString
			? `\n\nLähdemateriaalissa on myös kuvia. Käytä kuvia apuna FAQ-sisällön laatimisessa:\n${request.sourceMaterial.imagesAsString}`
			: '';

		let userPrompt = '';

		if (
			request.sourceMaterial &&
			request.sourceMaterial.selectedArticles.length > 0
		) {
			userPrompt = `Tee tiedostohakuja lähdemateriaaliin hakutyökalulla löytääksesi tärkeimmät aiheet ja yksityiskohdat. Luo sitten ${numberOfQuestions} usein kysyttyä kysymystä ja vastausta lähdemateriaalin pohjalta.${includeImages}

Noudata seuraavia ohjeita:
- Käytä hakutyökalua löytääksesi lähdemateriaaleista keskeiset aiheet, termit ja konseptit
- Luo luonnollisia kysymyksiä, joita lukijat todennäköisesti kysyisivät näistä aiheista
- Kysymysten tulee kattaa materiaalin tärkeimmät ja kiinnostavimmat aiheet - älä tee kysymyksiä pelkästään yhdestä asiasta vaan yritä kattaa materiaalin kaikki aiheet.
- Lyhyet vastaukset ovat ytimekkäitä ja selkeitä (1-2 lausetta)
- Laajennetut vastaukset antavat lisäyksityiskohtia ja kontekstia lähdemateriaalista (5-10 lausetta)
- Varmista, että kysymykset ovat järkevässä tärkeysjärjestyksessä
- Käytä selkeää ja ymmärrettävää kieltä
- Perusta vastauksesi löydettyihin faktoihin ja tietoihin lähdemateriaalista

Palauta vastaus JSON-muodossa.`;
		} else {
			userPrompt = `Luo ${numberOfQuestions} usein kysyttyä kysymystä ja vastausta aiheesta: ${topic}

Noudata seuraavia ohjeita:
- Luo yleisiä ja hyödyllisiä kysymyksiä aiheesta
- Kysymysten tulee olla informatiivisia ja opettavaisia
- Lyhyet vastaukset ovat ytimekkäitä ja selkeitä
- Laajennetut vastaukset antavat lisäyksityiskohtia ja kontekstia
- Varmista, että kysymykset ovat järkevässä järjestyksessä
- Käytä selkeää ja ymmärrettävää kieltä

Palauta vastaus JSON-muodossa.`;
		}

		return {
			system: systemPrompt,
			user: userPrompt,
		};
	}

	/**
	 * Process the AI response and extract FAQ data
	 */
	protected processResponseCore(
		response: { rawResponse: string; json: any },
		parameters?: any,
	): GenerationResult {
		try {
			const jsonData = response.json || JSON.parse(response.rawResponse);

			if (!jsonData.faqItems || !Array.isArray(jsonData.faqItems)) {
				return {
					success: false,
					error: 'Invalid FAQ data structure in response',
				};
			}

			const gameData = new PluginFAQGameData();
			gameData.title = jsonData.title || 'Usein kysyttyä';

			// Process each FAQ item
			gameData.faqItems = jsonData.faqItems.map((item: any) => ({
				id: item.id || uuidv4(),
				question: item.question || 'Kysymys puuttuu',
				shortAnswer:
					item.shortAnswer || item.answer || 'Vastaus puuttuu',
				expandedAnswer:
					item.expandedAnswer ||
					item.detailedAnswer ||
					item.shortAnswer ||
					'Vastaus puuttuu',
			}));

			return {
				success: true,
				gameData: gameData,
			};
		} catch (error) {
			console.error('Error processing FAQ response:', error);
			return {
				success: false,
				error: 'Failed to process FAQ response',
			};
		}
	}

	/**
	 * Get default parameters for FAQ generation
	 */
	getDefaultParameters(): any {
		return {
			numberOfQuestions: 15,
			clearPrevious: true,
			questionTopic: '',
		};
	}

	/**
	 * Merge generated content with existing content
	 */
	override mergeContent(
		existing: PluginFAQGameData,
		generated: PluginFAQGameData,
		clearPrevious: boolean,
	): PluginFAQGameData {
		const result = new PluginFAQGameData();

		// Keep the title from existing or use generated
		result.title = existing.title || generated.title;

		if (clearPrevious) {
			// Replace all FAQs with generated ones
			result.faqItems = [...generated.faqItems];
		} else {
			// Append generated FAQs to existing ones
			result.faqItems = [...existing.faqItems, ...generated.faqItems];
		}

		return result;
	}
}
